import type { SvgIconComponent } from "@mui/icons-material";
import GridViewIcon from "@mui/icons-material/GridView";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";

/**
 * 最外層導覽的四個分節。**這是整站導覽的第一層，固定四個，不隨資料成長。**
 *
 * 為什麼固定四個：服務會長到七個以上，如果第一層是服務清單，那一層就會
 * 持續變長而且需要滾動 —— 那是清單，不是導覽。改成固定四節之後，
 * 「我在哪個大區塊」永遠是一眼可辨的，服務數量只影響第二層。
 *
 * 桌機呈現成最左邊的 icon rail，手機呈現成底部的懸浮列（同一份宣告，
 * 兩種排版），所以兩邊的順序與行為不可能不一致。
 *
 * `hasPanel` 決定點下去會不會帶出第二層：
 *
 *   總覽     無第二層 —— 直接就是一頁
 *   服務     有第二層 —— 服務清單與各服務的子頁
 *   通知     無第二層
 *   個人中心 無第二層
 */

export type NavSectionKey = "overview" | "services" | "notifications" | "account";

export interface NavSection {
  key: NavSectionKey;
  /** rail 的 tooltip 與底部列的文字標籤 */
  label: string;
  /** 未選中時的線框圖示 */
  icon: SvgIconComponent;
  /**
   * 選中時的實心圖示。
   *
   * 線框 → 實心的切換是類 App 導覽最容易辨識的「目前位置」訊號，比只靠
   * 顏色可靠：色盲使用者、以及在陽光下看手機的人都還能分辨形狀的差異。
   */
  activeIcon: SvgIconComponent;
  to: string;
  hasPanel: boolean;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "overview",
    label: "總覽",
    icon: SpaceDashboardOutlinedIcon,
    activeIcon: SpaceDashboardIcon,
    to: "/",
    hasPanel: false,
  },
  {
    key: "services",
    label: "服務",
    icon: GridViewOutlinedIcon,
    activeIcon: GridViewIcon,
    to: "/services",
    hasPanel: true,
  },
  {
    key: "notifications",
    label: "通知",
    icon: NotificationsNoneOutlinedIcon,
    activeIcon: NotificationsIcon,
    to: "/notifications",
    hasPanel: false,
  },
  {
    key: "account",
    label: "個人中心",
    icon: PersonOutlineOutlinedIcon,
    activeIcon: PersonIcon,
    to: "/account",
    hasPanel: false,
  },
];

const BY_KEY = new Map<NavSectionKey, NavSection>(
  NAV_SECTIONS.map((section) => [section.key, section]),
);

export function navSection(key: NavSectionKey): NavSection {
  // 非空的常數表，key 是聯集型別，所以這裡不可能是 undefined
  return BY_KEY.get(key)!;
}

/**
 * 目前的網址屬於哪一節。
 *
 * 服務的路徑（`/master`、`/master/items`）沒有共同前綴 —— 它們就掛在根底下，
 * 因為服務代碼即是網址的一部分（`/master/items` 比 `/services/master/items`
 * 好記，而且服務代碼在後端保證唯一）。代價是這裡必須拿著使用者實際有的
 * 服務代碼來比對，不能只看前綴。
 *
 * 對不上任何一節時回 undefined（例如 404 頁）—— 那時候 rail 上不會有任何
 * 一項亮著，這比隨便亮一個更誠實。
 */
export function activeSection(
  pathname: string,
  serviceCodes: readonly string[] = [],
): NavSection | undefined {
  if (pathname === "/") {
    return navSection("overview");
  }

  const fixed = NAV_SECTIONS.find(
    (section) =>
      section.to !== "/" && (pathname === section.to || pathname.startsWith(`${section.to}/`)),
  );
  if (fixed) {
    return fixed;
  }

  const first = pathname.split("/")[1];
  return first && serviceCodes.includes(first) ? navSection("services") : undefined;
}
